import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import * as d3 from 'd3';
import { IMultiCharts } from '../multi-charts/Imulti-charts';

@Component({
  selector: 'app-step-line-chart',
  templateUrl: './step-line-chart.component.html',
  styleUrl: './step-line-chart.component.scss',
})
export class StepLineChartComponent implements OnChanges {
  @Input() data: { days: number; price: number; date: string }[];
  @Input() chartConfig: IMultiCharts;
  @ViewChild('chartContainer', { static: true }) container: ElementRef;
  @ViewChild('chartParent', { static: true }) chartParent: ElementRef;

  ngOnChanges(changes?: SimpleChanges): void {
    void changes;
    if (this.data && this.data.length > 0) {
      this.drawStepLine();
    } else {
      this.drawNoData();
    }
  }

  drawNoData() {
    const svg = d3.select(this.chartParent.nativeElement);
    svg.selectAll('svg').remove();
  }

  drawStepLine() {
    // Set up the dimensions and margins of the chart
    const margin = { top: 20, right: 30, bottom: 40, left: 60 };
    const width = this.chartConfig.width;
    const height = this.chartConfig.height - margin.top - margin.bottom;
    const innerWidth = width - margin.left - margin.right;

    // Create the SVG container and set the origin
    d3.select(this.chartParent.nativeElement).selectAll('svg').remove();
    const svg = d3
      .select(this.chartParent.nativeElement)
      .append('svg')
      .attr('width', width)
      .attr('height', this.chartConfig.height)
      .append('g')
      .attr('height', height)
      .attr('width', innerWidth)
      .attr('transform', `translate(${margin.left},${margin.top / 2})`);
    const maxPrice = Math.max(...this.data.map((item) => Math.abs(item.price)));

    // Set up the x and y scales
    const x = d3.scaleLinear().domain([499, 0]).range([0, innerWidth]);

    const y = d3.scaleLinear().domain([-maxPrice, maxPrice]).range([height, 0]);

    const xAxis = d3
      .axisBottom(x)
      .ticks(3)
      .tickValues([499, 0])
      .tickSizeInner(0)
      .tickSizeOuter(0)
      .tickFormat((d) => {
        return (d === 0 ? d : '-' + d) + ' days';
      })
      .tickPadding(15);

    const yAxis = d3
      .axisLeft(y)
      .ticks(3)
      .tickValues([-maxPrice, 0, maxPrice])
      .tickSizeInner(-innerWidth)
      .tickSizeOuter(0)
      .tickFormat((d) => {
        return d.toFixed(0) + '%';
      })
      .tickPadding(5);

    // Add the x axis
    svg.append('g').attr('class', 'x-axis').attr('transform', `translate(0,${height})`).call(xAxis);

    // Add the y axis
    svg.append('g').attr('class', 'y-axis').attr('height', height).call(yAxis);

    // Define the step line function
    const step = d3
      .line()
      .x((d) => x(d.days))
      .y((d) => y(d.price))
      .curve(d3.curveStepBefore);

    svg
      .append('path')
      .datum(this.data) // Bind the data to the path
      .attr('class', 'step')
      .attr('d', step);

    // Tooltip
    const tooltip = d3
      .select(this.chartParent.nativeElement)
      .append('div')
      .attr('class', 'step-tooltip')
      .style('opacity', 0);

    svg
      .selectAll('.step-point')
      .data(this.data)
      .enter()
      .append('circle')
      .attr('class', 'step-point')
      .attr('cx', (d) => x(d.days))
      .attr('cy', (d) => y(d.price))
      .attr('r', 4)
      .style('fill', '#AA75AA')
      .style('cursor', 'pointer')
      .on('mouseover', (event, d) => {
        tooltip.transition().duration(200).style('opacity', 1);
        const daysToDeparture = d.days === 0 ? d.days : '-' + d.days;
        const formattedDate = d3.timeFormat('%m/%d/%Y')(d.date);
        tooltip.html(
          `Days To Departure: ${daysToDeparture}<br>Price: ${d.price.toFixed(1)}%<br>Date: ${formattedDate}`,
        );

        if (x(x.invert(event.offsetX - margin.left)) + 70 >= innerWidth) {
          tooltip.attr('style', null);
          tooltip
            .style('top', event.offsetY + 'px')
            .style('right', innerWidth - x(x.invert(event.offsetX - margin.left)) + 40 + 'px');
        } else {
          tooltip.attr('style', null);
          tooltip
            .style('top', event.offsetY + 'px')
            .style('left', x(x.invert(event.offsetX - margin.left)) + 70 + 'px');
        }
        //          .style('right', (innerWidth - (x(x.invert(event.offsetX - margin.left))) + 70) + 'px');
      })
      .on('mouseout', () => tooltip.transition().duration(500).style('opacity', 0));
  }
}
